import React from 'react';
import { BookOpen, LogOut, Menu, UserIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getUserDisplayName, getUserIdentifier } from '../utils/userUtils';
import {
  StyledTopBarContainer,
  StyledMenuButton,
  StyledUserInfoContainer,
  StyledUserInfoRow,
  StyledUserName,
  StyledUserEmail,
  StyledAvatarContainer,
  StyledNotAuthContainer,
  StyledChevron,
  StyledLogoutButton,
  StyledPopupContainer,
  StyledPopupContent,
  StyledProfileItem,
} from './TopBar/TopBar.styled';
import { Button, Modal } from './ui';
import {
  StyledActionButtons,
  StyledConfirmationContent,
  StyledConfirmationText,
  StyledLogoutConfirmation,
} from './Sidebar/LogoutConfirmation.styled';
import { Link, useNavigate } from 'react-router-dom';
import { useEnterprise } from '../contexts/EnterpriseContext';

interface TopBarProps {
  onToggleSidebar: () => void;
}

const TopBar: React.FC<TopBarProps> = ({ onToggleSidebar }) => {
  const { isAuthenticated, logout, user } = useAuth();
  const { selectedEnterprise } = useEnterprise();
  const [isPopupOpen, setIsOpenPopup] = React.useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = React.useState(false);
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);
  const popupRef = React.useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Handle clicks outside the popup
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        setIsOpenPopup(false);
      }
    };

    if (isPopupOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPopupOpen]);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      // AuthContext.logout() posts to /auth/logout and clears credentials via
      // AuthService, which owns storage cleanup in a finally block. Do not
      // clear localStorage here — duplicating the keys risks them going stale.
      await logout();
    } catch (error) {
      console.error('Log out failed:', error);
    } finally {
      navigate('/login', { replace: true });
      setIsLoggingOut(false);
    }
  };
  const handleConfirmLogout = () => {
    handleLogout();
  };

  const handleCancelLogout = () => {
    if (!isLoggingOut) {
      setIsLogoutModalOpen(false);
    }
  };

  return (
    <StyledTopBarContainer>
      <div className="flex gap-4 items-center">
        <StyledMenuButton onClick={onToggleSidebar} aria-label="Toggle sidebar">
          <Menu size={24} />
        </StyledMenuButton>

        {/* show enterprise name for enterprise admin */}
        {user?.role === 'ENTERPRISE_ADMIN' && (
          <h1 className="heading-3-bold">{user.enterprise_name || selectedEnterprise?.name}</h1>
        )}
        {/* show enterprise name for enterprise admin */}
        {/* {user?.role === 'BRAND_ADMIN' && (
          <h1 className="heading-3-bold">{user.brand_name}</h1>
        )} */}
      </div>

      {isAuthenticated && user ? (
        <StyledUserInfoContainer ref={popupRef}>
          {/* User Info */}

          <StyledUserInfoRow
            onClick={() => {
              setIsOpenPopup(!isPopupOpen);
            }}
          >
            <StyledAvatarContainer>
              {user?.name?.trim()
                ? getUserDisplayName(user)?.charAt(0).toUpperCase()
                : getUserIdentifier(user)?.charAt(0).toUpperCase()}
            </StyledAvatarContainer>
            <StyledChevron $isExpanded={isPopupOpen} size={20} />
          </StyledUserInfoRow>

          {/* popup opens on user avatar click */}
          {isPopupOpen && (
            <StyledPopupContainer>
              <StyledPopupContent>
                <StyledUserName>{getUserDisplayName(user)}</StyledUserName>
                <StyledUserEmail>{getUserIdentifier(user)}</StyledUserEmail>
                {user?.role === 'ENTERPRISE_ADMIN' && (
                  <Link
                    to={'/traveller-catalog-guide'}
                    onClick={() => setIsOpenPopup(false)}
                  >
                    <StyledProfileItem>
                      <span className="p-1 bg-gray-50 rounded-full">
                        <BookOpen size={20} />
                      </span>
                      Catalog View
                    </StyledProfileItem>
                  </Link>
                )}
                <Link to={'/profile'} onClick={() => setIsOpenPopup(false)}>
                  <StyledProfileItem>
                    <span className="p-1 bg-gray-50 rounded-full">
                      <UserIcon size={20} />
                    </span>
                    Profile
                  </StyledProfileItem>
                </Link>
              </StyledPopupContent>
              <StyledLogoutButton onClick={() => setIsLogoutModalOpen(true)}>
                <LogOut size={20} />
                <span>Log out</span>
              </StyledLogoutButton>
            </StyledPopupContainer>
          )}
          {/* Logout Confirmation Modal */}
          <Modal
            isOpen={isLogoutModalOpen}
            onClose={handleCancelLogout}
            title="Confirm Log out"
            size="sm"
            closeOnBackdrop={!isLoggingOut}
          >
            <StyledLogoutConfirmation>
              <StyledConfirmationContent>
                <h3>Are you sure you want to log out?</h3>
                <StyledConfirmationText>
                  You will be redirected to the log in page and need to sign in
                  again.
                </StyledConfirmationText>
              </StyledConfirmationContent>

              <StyledActionButtons>
                <Button
                  variant="outline"
                  onClick={handleCancelLogout}
                  disabled={isLoggingOut}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={handleConfirmLogout}
                  disabled={isLoggingOut}
                >
                  {isLoggingOut ? 'Wait...' : 'Log out'}
                </Button>
              </StyledActionButtons>
            </StyledLogoutConfirmation>
          </Modal>
        </StyledUserInfoContainer>
      ) : (
        <StyledNotAuthContainer>
          <p>Not authenticated</p>
        </StyledNotAuthContainer>
      )}
    </StyledTopBarContainer>
  );
};

export default TopBar;
